import React, { useState } from "react";
import { FileCheck, Plus } from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { useMaterials } from "../../../hooks/useMaterials";

const MaterialsView = () => {
  const { activeProject } = useProjectContext();
  const { materials, loading, creating, error, create } = useMaterials();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    test_number: "",
    test_date: "",
    material_type: "",
    test_type: "",
    location: "",
    result: "",
    measured_value: "",
    required_value: "",
    test_age: "",
    laboratory: "",
    tested_by: "",
    notes: "",
  });

  if (!activeProject) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    await create({
      ...form,
      test_date: form.test_date,
    });
    setShowForm(false);
    setForm({
      test_number: "",
      test_date: "",
      material_type: "",
      test_type: "",
      location: "",
      result: "",
      measured_value: "",
      required_value: "",
      test_age: "",
      laboratory: "",
      tested_by: "",
      notes: "",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Materials</h2>
          <p className="text-gray-600">Track materials, approvals, and test results</p>
        </div>
        <FileCheck className="w-8 h-8 text-teal-500" />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Project: <strong>{activeProject.name}</strong>
        </div>

        <button
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
        >
          <Plus className="w-4 h-4" />
          New Material Test
        </button>
      </div>

      {showForm && (
        <form onSubmit={onSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Test Number"
              value={form.test_number} onChange={(e) => setForm((p) => ({ ...p, test_number: e.target.value }))} />
            <input type="date" className="border rounded-md px-3 py-2 text-sm" required
              value={form.test_date} onChange={(e) => setForm((p) => ({ ...p, test_date: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Material Type (e.g., Concrete)" required
              value={form.material_type} onChange={(e) => setForm((p) => ({ ...p, material_type: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Test Type (e.g., Slump)" required
              value={form.test_type} onChange={(e) => setForm((p) => ({ ...p, test_type: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Location"
              value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Result (pass/fail)"
              value={form.result} onChange={(e) => setForm((p) => ({ ...p, result: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Measured Value"
              value={form.measured_value} onChange={(e) => setForm((p) => ({ ...p, measured_value: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Required Value"
              value={form.required_value} onChange={(e) => setForm((p) => ({ ...p, required_value: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Test Age"
              value={form.test_age} onChange={(e) => setForm((p) => ({ ...p, test_age: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Laboratory"
              value={form.laboratory} onChange={(e) => setForm((p) => ({ ...p, laboratory: e.target.value }))} />
            <input className="border rounded-md px-3 py-2 text-sm" placeholder="Tested By"
              value={form.tested_by} onChange={(e) => setForm((p) => ({ ...p, tested_by: e.target.value }))} />
            <textarea className="border rounded-md px-3 py-2 text-sm md:col-span-3" rows={2} placeholder="Notes"
              value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" disabled={creating}
              className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-black disabled:opacity-60">
              {creating ? "Creating..." : "Create"}
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-md bg-gray-100 text-gray-800 text-sm font-medium hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b text-sm font-medium text-gray-800">Material Tests</div>

        {loading && <div className="p-4 text-sm text-gray-500">Loading...</div>}
        {error && <div className="p-4 text-sm text-red-600">Failed to load materials.</div>}
        {!loading && !error && materials.length === 0 && <div className="p-4 text-sm text-gray-500">No material tests yet.</div>}

        {!loading && !error && materials.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left p-3">Test #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Material</th>
                  <th className="text-left p-3">Test</th>
                  <th className="text-left p-3">Result</th>
                  <th className="text-left p-3">Location</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="p-3">{m.test_number || m.id}</td>
                    <td className="p-3">{m.test_date}</td>
                    <td className="p-3">{m.material_type}</td>
                    <td className="p-3">{m.test_type}</td>
                    <td className="p-3">{m.result || "-"}</td>
                    <td className="p-3">{m.location || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialsView;